import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import apiClient from '@/services/api';
import { useToast } from '@/components/common/Toast';

/**
 * The drive-time areas kept (Veramenta docs/en/60 sec.7): what the drive-time functions drew once and keep
 * for the next analysis from the same road. Forget them when the road network is loaded again.
 */
interface Kept {
  catchmentType: string;
  areas: number;
}

const TYPES: Record<string, number> = { walk: 1, drive: 2, drive_hv: 4, motor: 5, drive_nt: 6, drive_vhv: 7 };

/** A kind's travel type: Turkey's kinds are bare, other networks' are kind|network|direction|stay-XX. */
const typeOf = (kind: string): number | undefined => TYPES[kind.split('|')[0]];

const CacheManagement: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { data: kept = [], isLoading } = useQuery({
    queryKey: ['analysisCache'],
    queryFn: async () => (await apiClient.get<Kept[]>('/analysiscache')).data,
  });

  const forget = useMutation({
    mutationFn: (types: number[] | null) => apiClient.put('/analysiscache', types === null
      ? { deletionType: 'CLEAR_ALL_ANALYSIS_CACHE' }
      : { deletionType: 'CLEAR_ONLY_GIVEN_CATCHMENT_TYPE', catchmentTypes: types }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analysisCache'] });
      addToast(t('cache.cleared', { defaultValue: 'Forgotten' }), 'success');
    },
    onError: () => addToast(t('cache.clear_failed', { defaultValue: 'Could not forget them' }), 'error'),
  });

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-bold mb-1">{t('admin.cache')}</h2>
      <p className="text-sm text-gn-muted mb-4">
        {t('cache.hint', { defaultValue: 'Drive-time areas are drawn once and kept for the next analysis from the same point of the road network. Forget them after the road network has been loaded again.' })}
      </p>
      {isLoading && <p>…</p>}
      <table className="w-full text-sm mb-4">
        <thead>
          <tr className="text-left text-gn-muted"><th>{t('cache.kind', { defaultValue: 'Travel mode' })}</th><th>{t('cache.areas', { defaultValue: 'Areas kept' })}</th><th /></tr>
        </thead>
        <tbody>
          {kept.map((k) => (
            <tr key={k.catchmentType} className="border-t border-gn-light">
              <td className="py-2">{k.catchmentType}</td>
              <td className="py-2">{k.areas}</td>
              <td className="py-2 text-right">
                {typeOf(k.catchmentType) && (
                  <button type="button" className="px-3 py-1 border rounded" onClick={() => forget.mutate([typeOf(k.catchmentType)!])}>
                    {t('cache.forget', { defaultValue: 'Forget' })}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {kept.length === 0 && !isLoading && <tr><td colSpan={3} className="py-2 text-gn-muted">{t('cache.none', { defaultValue: 'Nothing is kept.' })}</td></tr>}
        </tbody>
      </table>
      <button type="button" className="px-4 py-2 bg-gn-blue text-gn-inverse rounded" onClick={() => forget.mutate(null)}>
        {t('cache.forget_all', { defaultValue: 'Forget all' })}
      </button>
    </div>
  );
};

export default CacheManagement;
